import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CatalogAssignResult, CatalogSectionModel, CountryModel, HxCatalogService, HxUiLabelPipe } from 'hx-services';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef } from 'ng-zorro-antd/modal';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { CatalogNotifyService } from '../catalog/catalog-notify.service';
import { labelText } from '../catalog/catalog-text';

export interface AssignCard {
  id: number;
  title: string;
  countryId?: number;
}

export interface AssignSectionData {
  cards: AssignCard[];
  sections: CatalogSectionModel[];
  countries: CountryModel[];
}

interface SectionOption {
  id: number;
  text: string;
  draft: boolean;
}

interface SkippedRow {
  id: number;
  title: string;
  /** why the card was left out, in words */
  reason: string;
}

/** «В раздел…»: puts the chosen cards into one section (or takes them out of any section). The cards that cannot get an address stay out and are listed. */
@Component({
  selector: 'hc-assign-section-modal',
  templateUrl: './assign-section.modal.html',
  styleUrls: ['./assign-section.modal.less'],
  providers: [provideTranslocoScope('composite-products')],
  imports: [FormsModule, TranslocoPipe, HxUiLabelPipe, NzAlertComponent, NzButtonComponent, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent,
    NzModalFooterDirective, NzRadioGroupComponent, NzRadioComponent, NzSelectComponent, NzOptionComponent]
})
export class AssignSectionModal implements OnInit {
  protected readonly data: AssignSectionData = inject(NZ_MODAL_DATA);
  protected readonly modalRef = inject(NzModalRef);
  protected mode: 'set' | 'clear' = 'set';
  protected sectionId: number | null = null;
  protected options: SectionOption[] = [];
  protected country?: CountryModel;
  protected severalCountries = false;
  protected noSections = false;
  protected cardsText = '';
  protected isSaving = false;
  /** a section is chosen (or «убрать из раздела») */
  protected ready = false;
  protected assigned?: number;
  protected skipped: SkippedRow[] = [];
  private readonly catalog = inject(HxCatalogService);
  private readonly notify = inject(CatalogNotifyService);
  private readonly tr = inject(TranslocoService);

  ngOnInit(): void {
    const lang = this.tr.getActiveLang();
    const countryIds = [...new Set(this.data.cards.map(c => c.countryId))];
    this.severalCountries = countryIds.length > 1;
    this.country = this.data.countries.find(c => c.id === countryIds[0]);
    this.options = this.data.sections
      .filter(s => countryIds.length === 1 && s.countryId === countryIds[0])
      .map(s => ({id: s.id, text: labelText(lang, s.title), draft: !s.enabled}));
    this.noSections = !this.severalCountries && this.options.length === 0;
    const shown = this.data.cards.slice(0, 4).map(c => c.title).join(', ');
    this.cardsText = this.data.cards.length > 4 ? `${shown} …` : shown;
  }

  protected onChoice(): void {
    this.ready = this.mode === 'clear' || this.sectionId !== null;
  }

  protected async submit(): Promise<void> {
    if (this.isSaving || this.severalCountries || this.assigned !== undefined || !this.ready) {
      return;
    }
    this.isSaving = true;
    try {
      const result: CatalogAssignResult = await this.catalog.assignCardsToSection(this.data.cards.map(c => c.id), this.mode === 'set' ? this.sectionId ?? undefined : undefined);
      this.assigned = result.assigned;
      const titles = new Map(this.data.cards.map(c => [c.id, c.title] as const));
      this.skipped = result.skipped.map(s => ({id: s.compositeProductId, title: titles.get(s.compositeProductId) ?? `#${s.compositeProductId}`, reason: this.reasonText(s.reason)}));
      if (this.skipped.length === 0) {
        this.modalRef.close('success');
      }
    } catch (err) {
      this.notify.error(err);
    } finally {
      this.isSaving = false;
    }
  }

  /** the reason is a key of the backend (`catalog.card.noAddress`): its words, or the key itself when it is not known */
  private reasonText(reason: string): string {
    const key = `compositeProducts.errors.${reason}`;
    const text = this.tr.translate(key);
    return text === key ? reason : text;
  }
}
